import { Routes } from '@angular/router';

import { UserRoles } from '@core/consts/userRoles';
import { authGuard } from '@core/guards/auth.guard';
import { guestGuard } from '@core/guards/guest.guard';
import { roleGuard } from '@core/guards/role.guard';
import { DoctorProfileResolver } from '@pages/public/doctor-profile/doctor-profile.resolver';

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./pages/public/main-page/main-page.component').then((m) => m.MainPageComponent),
  },
  {
    path: 'sign-up',
    canMatch: [guestGuard],
    loadComponent: () => import('./pages/public/sign-up/sign-up.component').then((m) => m.SignupPageComponent),
  },
  {
    path: 'sign-up-facility',
    canMatch: [guestGuard],
    loadComponent: () =>
      import('./pages/public/sign-up-facility/sign-up-facility.component').then((m) => m.SignUpFacilityComponent),
  },
  {
    path: 'sign-in',
    canMatch: [guestGuard],
    loadComponent: () => import('./pages/public/sign-in/sign-in.component').then((m) => m.SigninComponent),
  },
  {
    path: 'recover',
    canMatch: [guestGuard],
    loadComponent: () =>
      import('./pages/public/recover-password/recover-password.component').then((m) => m.RecoverPasswordComponent),
  },
  {
    path: 'privacy-policy',
    loadComponent: () =>
      import('./pages/public/privacy-policy/privacy-policy.component').then((m) => m.PrivacyPolicyComponent),
  },
  {
    path: 'terms-conditions',
    loadComponent: () =>
      import('./pages/public/terms-and-conditions/terms-and-conditions.component').then(
        (m) => m.TermsAndConditionsComponent,
      ),
  },
  {
    path: 'newsletter-confirmation',
    loadComponent: () =>
      import(
        './pages/public/main-page/newsletter-subscription/newsletter-confirmation/newsletter-confirmation.component'
      ).then((m) => m.NewsletterConfirmationComponent),
  },
  {
    path: 'clinicians',
    loadComponent: () => import('./pages/public/our-doctors/our-doctors.component').then((m) => m.OurDoctorsComponent),
  },
  {
    path: 'clinicians/:id',
    loadComponent: () =>
      import('./pages/public/doctor-profile/doctor-profile.component').then((m) => m.DoctorProfileComponent),
    resolve: { profile: DoctorProfileResolver },
    runGuardsAndResolvers: 'paramsOrQueryParamsChange',
  },
  {
    path: 'coming-soon',
    loadComponent: () => import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
  },
  {
    path: 'forbidden',
    loadComponent: () => import('./pages/public/forbidden/forbidden.component').then((m) => m.ForbiddenComponent),
  },
  {
    path: 'sysadmin-panel',
    canMatch: [authGuard, roleGuard],
    data: { roles: [UserRoles.SYSADMIN] },
    loadComponent: () =>
      import('./pages/public/sysadmin-panel/sysadmin-panel.component').then((m) => m.SysadminPanelComponent),

    children: [
      {
        path: '',
        redirectTo: 'facility-management',
        pathMatch: 'full',
      },
      {
        path: 'facility-management',
        loadComponent: () =>
          import('./pages/public/sysadmin-panel/facility-management/facility-management.component').then(
            (m) => m.FacilityManagementComponent,
          ),
      },
      {
        path: 'patient-account-management',
        loadComponent: () =>
          import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
      },
      {
        path: 'subscription-management',
        loadComponent: () =>
          import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
      },
      {
        path: 'emr-repository',
        loadComponent: () =>
          import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
      },
      {
        path: 'reviews-moderation',
        loadComponent: () =>
          import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
      },
      {
        path: 'system-monitoring',
        loadComponent: () =>
          import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
      },
      {
        path: 'audit-logs',
        loadComponent: () =>
          import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
      },
      {
        path: 'facility/:id',
        loadComponent: () =>
          import('./pages/public/sysadmin-panel/facilities-list/facility-profile/facility-profile.component').then(
            (m) => m.FacilityProfileComponent,
          ),
      },
      {
        path: 'coming-soon',
        loadComponent: () =>
          import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
      },
    ],
  },
  {
    path: 'facility-portal',
    canMatch: [authGuard, roleGuard],
    data: { roles: [UserRoles.HEAD_OF_PRACTICE] },
    loadComponent: () =>
      import('./pages/public/facility-portal/facility-portal.component').then((m) => m.FacilityPortalComponent),

    children: [
      {
        path: '',
        loadComponent: () =>
          import(
            './pages/public/facility-portal/components/facility-portal-main-page/facility-portal-main-page.component'
          ).then((m) => m.FacilityPortalMainPageComponent),
      },
      {
        path: 'facility-management',
        loadComponent: () =>
          import('./pages/public/facility-portal/components/facility-management/facility-management.component').then(
            (m) => m.FacilityManagementComponent,
          ),
      },
      {
        path: 'account-management',
        loadComponent: () =>
          import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
      },
      {
        path: 'clinicians-management',
        loadComponent: () =>
          import('@pages/private/clinician-list/clinician-list.component').then((m) => m.ClinicianListComponent),
      },
      {
        path: 'patient-management',
        loadComponent: () =>
          import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
      },
      {
        path: 'appointments',
        loadComponent: () =>
          import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
      },
    ],
  },
  {
    path: 'facility-portal',
    loadComponent: () =>
      import('./pages/public/facility-portal/facility-portal.component').then((m) => m.FacilityPortalComponent),
  },
  {
    path: 'clinicians-dashboard',
    canMatch: [authGuard, roleGuard],
    data: { roles: [UserRoles.SYSADMIN, UserRoles.HEAD_OF_PRACTICE] },
    loadComponent: () =>
      import('@pages/private/clinician-list/clinician-list.component').then((m) => m.ClinicianListComponent),
  },
  {
    path: 'healthcare-facilities',
    loadComponent: () =>
      import('./pages/public/healthcare-facilities-list/healthcare-facilities-list.component').then(
        (m) => m.HealthcareFacilitiesListComponent,
      ),
  },
  {
    path: 'healthcare-facilities/:id',
    loadComponent: () =>
      import('./pages/public/healthcare-facilities/healthcare-facilities.component').then(
        (m) => m.HealthcareFacilitiesComponent,
      ),
  },
  {
    path: 'healthcare-facilities-filters',
    loadComponent: () =>
      import(
        './pages/public/healthcare-facilities-list/healthcare-facilities-mobile-filters/healthcare-facilities-mobile-filters.component'
      ).then((m) => m.HealthcareFacilitiesMobileFiltersComponent),
  },
  {
    path: 'patient-portal',
    canMatch: [authGuard, roleGuard],
    data: { roles: [UserRoles.PATIENT] },
    loadComponent: () =>
      import('./pages/public/patient-portal/patient-portal-main.component').then((m) => m.PatientPortalMainComponent),

    children: [
      {
        path: '',
        redirectTo: 'patient-dashboard',
        pathMatch: 'full',
      },
      {
        path: 'patient-dashboard',
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './pages/public/patient-portal/components/dashboard/patient-appointments/patient-appointments.component'
              ).then((m) => m.PatientAppointmentsComponent),
            pathMatch: 'full',
          },
          {
            path: '',
            loadComponent: () =>
              import(
                './pages/public/patient-portal/components/dashboard/medical-documents/medical-documents.component'
              ).then((m) => m.MedicalDocumentsComponent),
            outlet: 'secondBlock',
            pathMatch: 'full',
          },
        ],
      },
      {
        path: 'appointment-management',
        loadComponent: () =>
          import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
      },
      {
        path: 'medical-history',
        loadChildren: () =>
          import('./pages/public/patient-portal/components/medications/medications.route').then(
            (m) => m.MEDICATIONS_ROUTES,
          ),
      },
      {
        path: 'account-settings',
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('./pages/public/patient-portal/components/patient-profile/patient-profile.component').then(
                (m) => m.PatientProfileComponent,
              ),
          },
          {
            path: 'update-personal-information',
            loadComponent: () =>
              import(
                './pages/public/patient-portal/components/update-personal-information/update-personal-information.component'
              ).then((m) => m.UpdatePersonalInformationComponent),
          },
        ],
      },
      {
        path: 'payment-history',
        loadComponent: () =>
          import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
      },
      {
        path: 'coming-soon',
        loadComponent: () =>
          import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
      },
    ],
  },
  {
    path: 'clinician-portal',
    canMatch: [authGuard, roleGuard],
    data: { roles: [UserRoles.CLINICIAN] },
    loadComponent: () =>
      import('./pages/public/clinician-portal/clinician-portal.component').then((m) => m.ClinicianPortalComponent),

    children: [
      {
        path: '',
        redirectTo: 'clinician-main-page',
        pathMatch: 'full',
      },
      {
        path: 'clinician-main-page',
        loadComponent: () =>
          import('./pages/public/clinician-portal/components/clinician-main-page/clinician-main-page.component').then(
            (m) => m.ClinicianMainPageComponent,
          ),
      },
      {
        path: 'patient-access',
        loadComponent: () =>
          import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
        children: [
          {
            path: 'diagnosis-documentation',
            loadComponent: () =>
              import(
                './pages/public/diagnosis-documentation/components/diagnosis-documentation-list/diagnosis-documentation-list.component'
              ).then((m) => m.DiagnosisDocumentationListComponent),
          },
          {
            path: 'search-in-emr',
            loadComponent: () =>
              import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
          },
          {
            path: 'today-appointment',
            loadComponent: () =>
              import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
          },
          {
            path: 'e-prescription',
            loadComponent: () =>
              import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
          },
          {
            path: 'recent-patients',
            loadComponent: () =>
              import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
          },
        ],
      },
      {
        path: 'schedule-management',
        loadComponent: () =>
          import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
      },
      {
        path: 'clinician-tools',
        loadComponent: () =>
          import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
        children: [
          {
            path: 'drug-interaction-checker',
            loadComponent: () =>
              import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
          },
          {
            path: 'medical-calculator-suite',
            loadComponent: () =>
              import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
          },
          {
            path: 'clinical-guidelines',
            loadComponent: () =>
              import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
          },
        ],
      },
      {
        path: 'order-management',
        loadComponent: () =>
          import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
        children: [
          {
            path: 'lab-tests',
            loadComponent: () =>
              import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
          },
          {
            path: 'imaging-orders',
            loadComponent: () =>
              import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
          },
          {
            path: 'referral-templates',
            loadComponent: () =>
              import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
          },
        ],
      },
      {
        path: 'documentation-tools',
        loadComponent: () =>
          import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
        children: [
          {
            path: 'SOAP',
            loadComponent: () =>
              import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
          },
          {
            path: 'procedure-note-templates',
            loadComponent: () =>
              import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
          },
          {
            path: 'common-visit-type-templates',
            loadComponent: () =>
              import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
          },
          {
            path: 'custom-template-favorites',
            loadComponent: () =>
              import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
          },
          {
            path: 'quick-notes',
            loadComponent: () =>
              import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
          },
        ],
      },
      {
        path: 'communication-center',
        loadComponent: () =>
          import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
        children: [
          {
            path: 'message-to-patient',
            loadComponent: () =>
              import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
          },
          {
            path: 'staff-messaging',
            loadComponent: () =>
              import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
          },
          {
            path: 'critical-result-alert',
            loadComponent: () =>
              import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
          },
          {
            path: 'schedule-change-alert',
            loadComponent: () =>
              import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
          },
          {
            path: 'telemedicine-session',
            loadComponent: () =>
              import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
          },
        ],
      },
      {
        path: 'coming-soon',
        loadComponent: () =>
          import('./pages/public/coming-soon/coming-soon.component').then((m) => m.ComingSoonComponent),
      },
    ],
  },
];
